import { Temporal } from 'temporal-polyfill'

import type { ScheduleEvent } from '../../types'
import {
  createRRuleFromFormState,
  formatDateForInput,
  formatDateTimeForApi,
  formatTimeForInput,
  parseDateTimeFromInput,
  parseRRuleToFormState,
  type RepeatFormState,
  type ScheduleEventHelper,
} from '../../utils'

export interface OverrideFormState {
  name: string
  dateType: 'SINGLE_DAY' | 'MULTIPLE_DAYS'
  startDate: string
  endDate?: string
  isAllDay: boolean
  startTime?: string
  endTime?: string
  repeat: RepeatFormState
  routing: 'CLOSED' | 'HOLIDAY'
}

export interface DerivedStartDateValues {
  day: number
  month: number
  weekday: number
  setpos: number
}

export type OverrideValidationCode =
  | 'NAME_REQUIRED'
  | 'MULTI_DAY_END_DATE_REQUIRED'
  | 'START_END_TIME_REQUIRED'
  | 'END_MUST_BE_AFTER_START'
  | 'WEEKLY_WEEKDAY_REQUIRED'
  | 'MONTHLY_PATTERN_REQUIRED'
  | 'VALID_DAY_REQUIRED'
  | 'VALID_WEEKDAY_PATTERN_REQUIRED'
  | 'YEARLY_MONTH_REQUIRED'
  | 'REPEAT_COUNT_REQUIRED'
  | 'REPEAT_UNTIL_REQUIRED'

export type OverrideFormField =
  | 'name'
  | 'startDate'
  | 'endDate'
  | 'startTime'
  | 'endTime'
  | 'repeatFrequency'
  | 'repeatWeekdays'
  | 'repeatCount'
  | 'repeatUntil'

export const getOverrideValidationField = (
  code: OverrideValidationCode,
  formState: OverrideFormState,
): OverrideFormField => {
  switch (code) {
    case 'NAME_REQUIRED':
      return 'name'
    case 'MULTI_DAY_END_DATE_REQUIRED':
      return 'endDate'
    case 'START_END_TIME_REQUIRED':
      return formState.startTime ? 'endTime' : 'startTime'
    case 'END_MUST_BE_AFTER_START':
      if (
        formState.dateType === 'MULTIPLE_DAYS' &&
        formState.endDate &&
        formState.endDate < formState.startDate
      ) {
        return 'endDate'
      }
      return formState.isAllDay ? 'endDate' : 'endTime'
    case 'WEEKLY_WEEKDAY_REQUIRED':
      return 'repeatWeekdays'
    case 'MONTHLY_PATTERN_REQUIRED':
    case 'VALID_DAY_REQUIRED':
    case 'VALID_WEEKDAY_PATTERN_REQUIRED':
    case 'YEARLY_MONTH_REQUIRED':
      return 'repeatFrequency'
    case 'REPEAT_COUNT_REQUIRED':
      return 'repeatCount'
    case 'REPEAT_UNTIL_REQUIRED':
      return 'repeatUntil'
  }
}

export interface OverrideEventDateTimePayload {
  startZdt: Temporal.ZonedDateTime
  endZdt: Temporal.ZonedDateTime
}

export interface OverrideSavePayload {
  summary: string
  dtstart: string
  dtend: string
  duration: string
  rrule?: string | null
  rdate?: string | null
  exdate?: string | null
  switchboard_routing: 'CLOSED' | 'HOLIDAY'
  fbtype: 'BUSY' | 'BUSY-UNAVAILABLE'
  time_zone: string
}

export const getDefaultOverrideFormState = (): OverrideFormState => {
  return {
    name: '',
    dateType: 'SINGLE_DAY',
    startDate: new Date().toISOString().split('T')[0],
    endDate: undefined,
    isAllDay: true,
    startTime: undefined,
    endTime: undefined,
    repeat: {
      frequency: 'NONE',
      interval: 1,
      yearlyType: 'SPECIFIC_DATE',
      endType: 'NEVER',
    },
    routing: 'CLOSED',
  }
}

export const initializeOverrideFormState = (
  event: ScheduleEvent,
  eventHelper: ScheduleEventHelper,
): OverrideFormState => {
  const startZdt = eventHelper.zonedDateTimeStart
  const endZdt = eventHelper.zonedDateTimeEnd
  const isSingleDay = eventHelper.isSingleDayEvent()
  const isAllDay = eventHelper.isAllDayEvent()
  // All-day dtend values are exclusive. The form presents the final included
  // calendar day, so convert the stored boundary before initializing it.
  const formEndZdt =
    !isSingleDay && isAllDay ? endZdt.subtract({ days: 1 }) : endZdt
  const repeat = parseRRuleToFormState(event.rrule || null, event.dtstart || '')

  if (repeat.frequency === 'YEARLY' && !repeat.month) {
    repeat.month = startZdt.month
    if (repeat.yearlyType === 'SPECIFIC_DATE' && !repeat.day) {
      repeat.day = startZdt.day
    }
  }

  return {
    name: event.summary || '',
    dateType: isSingleDay ? 'SINGLE_DAY' : 'MULTIPLE_DAYS',
    startDate: formatDateForInput(startZdt),
    endDate: isSingleDay ? undefined : formatDateForInput(formEndZdt),
    isAllDay,
    startTime: isAllDay ? undefined : formatTimeForInput(startZdt),
    endTime: isAllDay ? undefined : formatTimeForInput(endZdt),
    repeat,
    routing: event.switchboard_routing === 'HOLIDAY' ? 'HOLIDAY' : 'CLOSED',
  }
}

export const getDerivedStartDateValues = (
  startDate: string,
): DerivedStartDateValues => {
  const [year, month, day] = startDate.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return {
    day,
    month,
    weekday: date.getDay(),
    setpos: Math.ceil(day / 7),
  }
}

export const buildOverrideDateTimePayload = (
  formState: OverrideFormState,
  scheduleTimeZone: string,
): OverrideEventDateTimePayload => {
  const startZdt = parseDateTimeFromInput(
    formState.startDate,
    formState.isAllDay ? '00:00' : formState.startTime || '00:00',
    scheduleTimeZone,
  )

  let endZdt: Temporal.ZonedDateTime
  if (formState.dateType === 'MULTIPLE_DAYS' && formState.endDate) {
    endZdt = parseDateTimeFromInput(
      formState.endDate,
      formState.isAllDay ? '00:00' : formState.endTime || '23:59',
      scheduleTimeZone,
    )
    if (formState.isAllDay) {
      endZdt = endZdt.add({ days: 1 })
    }
  } else if (formState.isAllDay) {
    endZdt = startZdt.add({ days: 1 })
  } else {
    endZdt = parseDateTimeFromInput(
      formState.startDate,
      formState.endTime || '23:59',
      scheduleTimeZone,
    )
  }

  return { startZdt, endZdt }
}

export const validateOverrideFormState = (
  formState: OverrideFormState,
): OverrideValidationCode | null => {
  if (!formState.name.trim()) {
    return 'NAME_REQUIRED'
  }

  if (formState.dateType === 'MULTIPLE_DAYS' && !formState.endDate) {
    return 'MULTI_DAY_END_DATE_REQUIRED'
  }

  if (!formState.isAllDay && (!formState.startTime || !formState.endTime)) {
    return 'START_END_TIME_REQUIRED'
  }

  if (
    formState.repeat.frequency === 'WEEKLY' &&
    (!formState.repeat.byweekday || formState.repeat.byweekday.length === 0)
  ) {
    return 'WEEKLY_WEEKDAY_REQUIRED'
  }

  if (formState.repeat.frequency === 'MONTHLY') {
    if (!formState.repeat.monthlyType) {
      return 'MONTHLY_PATTERN_REQUIRED'
    }

    if (formState.repeat.monthlyType === 'SPECIFIC_DATE') {
      if (
        !formState.repeat.day ||
        formState.repeat.day < 1 ||
        formState.repeat.day > 31
      ) {
        return 'VALID_DAY_REQUIRED'
      }
    } else if (formState.repeat.monthlyType === 'NTH_WEEKDAY') {
      if (
        formState.repeat.setpos === undefined ||
        formState.repeat.weekday === undefined
      ) {
        return 'VALID_WEEKDAY_PATTERN_REQUIRED'
      }
    }
  }

  if (formState.repeat.frequency === 'YEARLY') {
    if (!formState.repeat.month) {
      return 'YEARLY_MONTH_REQUIRED'
    }

    if (formState.repeat.yearlyType === 'SPECIFIC_DATE') {
      if (
        !formState.repeat.day ||
        formState.repeat.day < 1 ||
        formState.repeat.day > 31
      ) {
        return 'VALID_DAY_REQUIRED'
      }
    } else if (formState.repeat.yearlyType === 'NTH_WEEKDAY') {
      if (
        formState.repeat.setpos === undefined ||
        formState.repeat.weekday === undefined
      ) {
        return 'VALID_WEEKDAY_PATTERN_REQUIRED'
      }
    }
  }

  if (formState.repeat.endType === 'AFTER' && !formState.repeat.count) {
    return 'REPEAT_COUNT_REQUIRED'
  }

  if (formState.repeat.endType === 'ON_DATE' && !formState.repeat.until) {
    return 'REPEAT_UNTIL_REQUIRED'
  }

  return null
}

export const validateDateTimeOrder = (
  startZdt: Temporal.ZonedDateTime,
  endZdt: Temporal.ZonedDateTime,
): OverrideValidationCode | null => {
  if (Temporal.ZonedDateTime.compare(endZdt, startZdt) <= 0) {
    return 'END_MUST_BE_AFTER_START'
  }

  return null
}

export const buildOverrideSavePayload = (
  formState: OverrideFormState,
  startZdt: Temporal.ZonedDateTime,
  endZdt: Temporal.ZonedDateTime,
  scheduleTimeZone: string,
): OverrideSavePayload => {
  const dtstart = formatDateTimeForApi(startZdt)
  const dtend = formatDateTimeForApi(endZdt)
  const duration = Temporal.Duration.from({
    milliseconds: endZdt.epochMilliseconds - startZdt.epochMilliseconds,
  })
  const rrule = createRRuleFromFormState(formState.repeat, dtstart)

  // `undefined` omits recurrence from generic payloads. Edit flows set
  // `rrule: null` explicitly when clearing an existing repeat.
  return {
    summary: formState.name,
    dtstart,
    dtend,
    duration: duration.toString(),
    rrule: rrule || undefined,
    switchboard_routing: formState.routing,
    fbtype: 'BUSY-UNAVAILABLE',
    time_zone: scheduleTimeZone,
  }
}
